import { userService } from '@features/auth/services/userService';
import { User } from '@shared/types/user';
import { create } from 'zustand';

interface UserMapState {
  userMap: Record<string, User>;
  loadingUserIds: Set<string>;
  setUserMap: (map: Record<string, User>) => void;
  addUsers: (users: Record<string, User>) => void;
  getUser: (userId: string) => User | undefined;
  hasUser: (userId: string) => boolean;
  isLoading: (userId: string) => boolean;
  getMissingUserIds: (userIds: string[]) => string[];
  needsUpdate: (userIds: string[]) => boolean;
  fetchUsers: (userIds: string[], currentUser?: User) => Promise<Record<string, User>>;
  fetchUser: (userId: string) => Promise<User | undefined>;
  clear: () => void;
}

const pendingRequests: Map<string, Promise<User | undefined>> = new Map();

const createFetchPromise = (
  userId: string,
  addUsers: (users: Record<string, User>) => void,
  setLoadingState: (fn: (state: UserMapState) => Partial<UserMapState>) => void
): Promise<User | undefined> => {
  const fetchPromise = (async () => {
    try {
      setLoadingState((state) => ({
        loadingUserIds: new Set([...state.loadingUserIds, userId]),
      }));

      const user = await userService.getUserById(userId);
      if (user) {
        addUsers({ [userId]: user });
      }
      return user;
    } catch (error) {
      console.error(`Error fetching user ${userId}:`, error);
      return undefined;
    } finally {
      pendingRequests.delete(userId);
      setLoadingState((state) => {
        const newSet = new Set(state.loadingUserIds);
        newSet.delete(userId);
        return { loadingUserIds: newSet };
      });
    }
  })();

  return fetchPromise;
};

export const useUserMapStore = create<UserMapState>((set, get) => ({
  userMap: {},
  loadingUserIds: new Set(),

  setUserMap: (map) => set({ userMap: map }),

  addUsers: (users) =>
    set((state) => ({
      userMap: { ...state.userMap, ...users },
    })),

  getUser: (userId) => get().userMap[userId],

  hasUser: (userId) => userId in get().userMap,

  isLoading: (userId) =>
    get().loadingUserIds.has(userId) || pendingRequests.has(userId),

  getMissingUserIds: (userIds) => {
    const { userMap } = get();
    return userIds.filter((id) => !(id in userMap) && !pendingRequests.has(id));
  },

  needsUpdate: (userIds) => {
    const { userMap } = get();
    return userIds.some((id) => !(id in userMap) && !pendingRequests.has(id));
  },

  fetchUser: (userId) => {
    const { userMap, addUsers } = get();

    if (userMap[userId]) {
      return Promise.resolve(userMap[userId]);
    }

    if (pendingRequests.has(userId)) {
      return pendingRequests.get(userId)!;
    }

    const fetchPromise = createFetchPromise(userId, addUsers, set);
    pendingRequests.set(userId, fetchPromise);
    return fetchPromise;
  },

  fetchUsers: async (userIds, currentUser) => {
    const { userMap, addUsers, fetchUser } = get();
    const result: Record<string, User> = {};

    if (currentUser && userIds.includes(currentUser.id)) {
      result[currentUser.id] = currentUser;
      if (!userMap[currentUser.id]) {
        addUsers({ [currentUser.id]: currentUser });
      }
    }

    const missingIds = userIds.filter(
      (id) => !(id in userMap) && !(currentUser && id === currentUser.id)
    );

    if (missingIds.length > 0) {
      const fetchPromises = missingIds.map((id) => fetchUser(id));
      const users = await Promise.all(fetchPromises);

      users.forEach((user, index) => {
        if (user) {
          result[missingIds[index]] = user;
        }
      });
    }

    userIds.forEach((id) => {
      if (userMap[id] && !result[id]) {
        result[id] = userMap[id];
      }
    });

    return result;
  },

  clear: () => {
    pendingRequests.clear();
    set({ userMap: {}, loadingUserIds: new Set() });
  },
}));
